import { scanStructure } from '../support';
import type { RepairContext, RepairProposal, RepairRule } from '../types';

/**
 * Re-emit a `'single-quoted'` literal as a JSON string: `{'a':'b'}` → `{"a":"b"}`.
 *
 * Two facts make this safe, and the legacy rule had neither.
 *
 * 1. **Where the literal starts is a token boundary the scanner drew.** A `'` inside a
 *    `String` token is an apostrophe in somebody's data (`{"a":"it's fine"}`) and is never
 *    looked at. The legacy guard was "does any quoted span in the document open with `'`",
 *    which re-delimited *every* span in the file once one did.
 * 2. **The value is decoded and then re-encoded with `JSON.stringify`.** `{'a':'he said
 *    "hi"'}` becomes `{"a":"he said \"hi\""}`. The legacy replacer emitted the content raw
 *    and produced `{"a":"he said "hi""}` — broken JSON from valid input (recon §3.4, B7).
 *
 * `jsonc-parser` splits `'he said "hi"'` into five tokens (`'he`, `said`, `"hi"`, `'`), so
 * the literal is read forward from its opening quote with escape tracking rather than taken
 * from a single token. Starting at a boundary the scanner classified as non-string is what
 * makes reading across those tokens legitimate.
 */
export const requoteSingleQuotedRule: RepairRule = {
  id: 'requote-single-quoted',
  confidence: 'safe',
  priority: 31,
  resolves: ['string/single-quoted'],

  detect(ctx: RepairContext): readonly RepairProposal[] {
    return scanStructure(ctx.source, ctx.tokens)
      .foreign.filter((literal) => literal.open === "'")
      .map((literal) => ({
        ruleId: 'requote-single-quoted',
        span: { from: literal.from, to: literal.to },
        edits: [{ from: literal.from, to: literal.to, insert: JSON.stringify(literal.value) }],
        labelCode: 'fix.requote-single-quoted',
        resolves: 'string/single-quoted',
      }));
  },
};
